//src/sinifaraclari/SpinWheel.js

import { useEffect, useRef, useState, useCallback, useMemo } from 'react';

const SpinWheel = () => {
  const [names, setNames] = useState(['Ali', 'Ayşe', 'Mehmet', 'Fatma', 'Can']);
  const [inputName, setInputName] = useState('');
  const [winner, setWinner] = useState(null);
  const [isSpinning, setIsSpinning] = useState(false);
  const canvasRef = useRef(null);
  const animationFrameId = useRef(null);
  const spinDuration = 5000;

  const [spinSound, winnerSound] = useMemo(() => [
    new Audio('sesler/spin-sound.mp3'),
    new Audio('sesler/winner-sound.mp3')
  ], []);

  const namePool = useMemo(() => [
    'Zeynep', 'Emre', 'Elif', 'Murat', 'Seda', 'Hasan', 'Buse', 'Ahmet', 'Ece', 'Okan',
    'Deniz', 'Ceren', 'Kaan', 'Selin', 'Burak'
  ], []);

  const easeOutQuart = (t) => 1 - Math.pow(1 - t, 4);

  const drawWheel = useCallback((angle = 0) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const radius = canvas.width / 2;
    const segmentAngle = (2 * Math.PI) / names.length;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    names.forEach((name, index) => {
      const startAngle = index * segmentAngle + angle;
      const endAngle = startAngle + segmentAngle;
      ctx.beginPath();
      ctx.moveTo(radius, radius);
      ctx.arc(radius, radius, radius, startAngle, endAngle);
      ctx.fillStyle = `hsl(${(index * 360) / names.length}, 70%, 60%)`;
      ctx.fill();
      ctx.strokeStyle = '#fff';
      ctx.lineWidth = 2;
      ctx.stroke();

      ctx.save();
      ctx.translate(radius, radius);
      ctx.rotate(startAngle + segmentAngle / 2);
      ctx.fillStyle = 'white';
      ctx.font = '22px Arial';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(name, radius - 65, 0);
      ctx.restore();
    });

    ctx.beginPath();
    ctx.arc(radius, radius, 25, 0, 2 * Math.PI);
    ctx.fillStyle = '#fff';
    ctx.fill();
    ctx.strokeStyle = '#333';
    ctx.lineWidth = 3;
    ctx.stroke();

    ctx.beginPath();
    ctx.moveTo(radius, 25);
    ctx.lineTo(radius - 20, 60);
    ctx.lineTo(radius + 20, 60);
    ctx.closePath();
    ctx.fillStyle = '#ff4d4d';
    ctx.fill();
    ctx.strokeStyle = '#333';
    ctx.lineWidth = 2;
    ctx.stroke();
  }, [names]);

  const spinWheel = useCallback(() => {
    if (names.length === 0 || isSpinning) return;
    
    setIsSpinning(true);
    
    if (animationFrameId.current) {
      cancelAnimationFrame(animationFrameId.current);
    }

    spinSound.currentTime = 0;
    spinSound.play().catch(err => console.error('Spin sound error:', err));

    const randomAngle = Math.random() * 360 + 1080;
    const startTime = performance.now();

    const animate = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / spinDuration, 1);
      const easedProgress = easeOutQuart(progress);
      const angle = randomAngle * easedProgress;
      drawWheel((angle * Math.PI) / 180);

      if (progress < 1) {
        animationFrameId.current = requestAnimationFrame(animate);
      } else {
        setIsSpinning(false);
        spinSound.pause();
        spinSound.currentTime = 0;

        // Calculate the winner
        const finalAngleDegrees = (angle % 360 + 360) % 360;
        const segmentAngleDegrees = 360 / names.length;

        // Adjust for canvas coordinate system (0 degrees at 3 o'clock) and pointer at top (0 degrees visual)
        // Canvas 0 degrees is at 3 o'clock, so we need to shift by 90 degrees to align with the pointer at 12 o'clock
        const adjustedAngle = (finalAngleDegrees + 90) % 360;
        // Calculate the segment index (invert the angle to match clockwise segment ordering)
        const winnerIndex = Math.floor((360 - adjustedAngle) / segmentAngleDegrees) % names.length;

        setWinner(names[winnerIndex]);
        
        winnerSound.currentTime = 0;
        winnerSound.play().catch(err => console.error('Winner sound error:', err));
      }
    };

    animationFrameId.current = requestAnimationFrame(animate);
  }, [names, isSpinning, drawWheel, spinSound, winnerSound]);

  const setNumberWheel = useCallback((max) => {
    const numberList = Array.from({ length: max }, (_, i) => (i + 1).toString());
    setNames(numberList);
  }, []);

  const handleAddName = useCallback((e) => {
    if ((e.key === 'Enter' || e.type === 'click') && inputName.trim()) {
      setNames(prev => [...prev, inputName.trim()]);
      setInputName('');
    }
  }, [inputName]);

  const addRandomName = useCallback(() => {
    const randomIndex = Math.floor(Math.random() * namePool.length);
    setNames(prev => [...prev, namePool[randomIndex]]);
  }, [namePool]);

  const addRandomThreeNames = useCallback(() => {
    const shuffledPool = [...namePool].sort(() => Math.random() - 0.5);
    const newNames = shuffledPool.slice(0, 3).filter(name => !names.includes(name));
    setNames(prev => [...prev, ...newNames]);
  }, [namePool, names]);

  const removeLastName = useCallback(() => {
    setNames(prev => prev.slice(0, -1));
  }, []);

  const clearAllNames = useCallback(() => {
    setNames([]);
  }, []);

  const handleRemoveName = useCallback((index) => {
    setNames(prev => prev.filter((_, i) => i !== index));
  }, []);

  const closeWinner = useCallback(() => {
    setWinner(null);
    winnerSound.pause();
    winnerSound.currentTime = 0;
  }, [winnerSound]);

  const removeWinner = useCallback(() => {
    setNames(prev => prev.filter(name => name !== winner));
    setWinner(null);
    winnerSound.pause();
    winnerSound.currentTime = 0;
  }, [winner, winnerSound]);

  useEffect(() => {
    drawWheel();
    
    return () => {
      if (animationFrameId.current) {
        cancelAnimationFrame(animationFrameId.current);
      }
    };
  }, [drawWheel]);

  return (
    <div className="sw-container">
      <div className="sw-controls-panel">
        <div className="sw-button-group">
          <h3 className="sw-title">Sayı Çarkları</h3>
          <div className="sw-button-row">
            {[4, 5, 6, 7, 8, 9, 10, 12].map(num => (
              <button
                key={num}
                onClick={() => setNumberWheel(num)}
                className="sw-action-btn sw-number-btn"
              >
                1-{num}
              </button>
            ))}
          </div>
        </div>

        <div className="sw-button-group">
          <h3 className="sw-title">İşlemler</h3>
          <div className="sw-button-row">
            <button
              onClick={addRandomName}
              className="sw-action-btn sw-feature-btn"
            >
              Rastgele İsim
            </button>
            <button
              onClick={addRandomThreeNames}
              className="sw-action-btn sw-feature-btn"
            >
              3 Rastgele İsim
            </button>
          </div>
          <div className="sw-button-row">
            <button
              onClick={removeLastName}
              className="sw-action-btn sw-feature-btn"
              disabled={names.length === 0}
            >
              Son İsmi Sil
            </button>
            <button
              onClick={clearAllNames}
              className="sw-action-btn sw-feature-btn"
              disabled={names.length === 0}
            >
              Tümünü Temizle
            </button>
          </div>
        </div>
      </div>

      <div className="sw-wheel-panel">
        <canvas
          ref={canvasRef}
          width="450"
          height="450"
          className="sw-wheel-canvas"
          onClick={spinWheel}
          aria-label="Spin wheel"
        ></canvas>
        <button
          onClick={spinWheel}
          disabled={isSpinning || names.length === 0}
          className="sw-spin-btn"
        >
          {isSpinning ? 'Dönüyor...' : 'Çarkı Çevir'}
        </button>
      </div>

      <div className="sw-names-panel">
        <div className="sw-name-list-container">
          <h3 className="sw-title">İsim Listesi</h3>
          <div className="sw-input-group">
            <input
              type="text"
              value={inputName}
              onChange={(e) => setInputName(e.target.value)}
              onKeyDown={handleAddName}
              placeholder="İsim ekle..."
              className="sw-input"
            />
            <button
              onClick={handleAddName}
              disabled={!inputName.trim()}
              className="sw-add-btn"
            >
              Ekle
            </button>
          </div>
          <ul className="sw-name-list">
            {names.map((name, index) => (
              <li key={index} className="sw-name-item">
                <span>{name}</span>
                <button
                  onClick={() => handleRemoveName(index)}
                  className="sw-remove-btn"
                >
                  Sil
                </button>
              </li>
            ))}
            {names.length === 0 && (
              <li className="text-muted text-center py-3">Henüz isim eklenmedi</li>
            )}
          </ul>
        </div>
      </div>

      {winner && (
        <div className="sw-winner-popup">
          <div className="sw-winner-content">
            <h2 className="sw-winner-title">Kazanan!</h2>
            <p className="sw-winner-name">{winner}</p>
            <div className="sw-popup-btns">
              <button
                onClick={closeWinner}
                className="sw-popup-btn sw-close-btn"
              >
                Kapat
              </button>
              <button
                onClick={removeWinner}
                className="sw-popup-btn sw-delete-btn"
              >
                Listeden Kaldır
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SpinWheel;